import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Heart, Cog, BookOpen, FileText, TrendingUp, Handshake, BarChart3 } from 'lucide-react';
import { useBanner } from '../contexts/BannerContext';

export const ResourcesDropdown: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isBannerVisible, bannerHeight } = useBanner();
  const location = useLocation();

  // Handle escape key to close menu
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    // Add event listeners when dropdown is hovered
    if (isHovered) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    // Cleanup event listeners
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isHovered]);

  // Show dropdown on hover only
  const shouldShowDropdown = isHovered;

  // Close dropdown and reset states when navigating
  const handleResourceClick = () => {
    setIsHovered(false);
  };

  // Helper function to close menu and reset all states
  const closeMenu = () => {
    setIsHovered(false);
  };

  const resources = {
    industries: [
      { icon: <Heart className="w-6 h-6" />, title: "Healthcare AI", description: "AI solutions for healthcare providers", href: "/industries/healthcare", color: "text-blue-600 dark:text-blue-400" },
      { icon: <Cog className="w-6 h-6" />, title: "Manufacturing AI", description: "Smart manufacturing solutions", href: "/industries/manufacturing", color: "text-blue-600 dark:text-blue-400" }
    ],
    blogs: [
      { icon: <BookOpen className="w-6 h-6" />, title: "AI Implementation Guide", description: "Step-by-step AI implementation", href: "/blog/ai-implementation-guide", color: "text-blue-600 dark:text-blue-400" },
      { icon: <FileText className="w-6 h-6" />, title: "All Blog Posts", description: "Latest insights and articles", href: "/blog", color: "text-blue-600 dark:text-blue-400" }
    ],
    business: [
      { icon: <BarChart3 className="w-6 h-6" />, title: "Case Studies", description: "Client success stories and results", href: "/case-studies", color: "text-blue-600 dark:text-blue-400" },
      { icon: <TrendingUp className="w-6 h-6" />, title: "Investors", description: "Business overview and opportunities", href: "/investors", color: "text-blue-600 dark:text-blue-400" },
      { icon: <Handshake className="w-6 h-6" />, title: "Partnerships", description: "Partnership opportunities", href: "/partnerships", color: "text-blue-600 dark:text-blue-400" }
    ]
  };

  return (
    <div 
      ref={dropdownRef}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span
        className={`relative font-medium transition-all duration-300 text-gray-700 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 ${
          location.pathname.startsWith("/industries/") || location.pathname.startsWith("/blog/") || location.pathname === "/case-studies" || location.pathname === "/investors" || location.pathname === "/partnerships" ? "text-blue-600 dark:text-blue-400" : ""
        }`}
      >
        Resources
        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 dark:bg-blue-400 transition-all duration-300 hover:w-full"></span>
      </span>

      {/* Dropdown Menu */}
      <div 
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ease-out ${
          shouldShowDropdown 
            ? 'opacity-100 visible transform translate-y-0 scale-100' 
            : 'opacity-0 invisible transform -translate-y-2 scale-98'
        }`}
        style={{ top: isBannerVisible ? `${bannerHeight + 23.5}px` : '64px' }}
      >
        <div className="container mx-auto px-4 max-w-4xl">
          <div className={`bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl shadow-xl dark:shadow-2xl overflow-hidden transition-all duration-300 ease-out ${
            shouldShowDropdown ? 'transform translate-y-0 opacity-100' : 'transform -translate-y-1 opacity-90'
          }`}>
            {/* Resources Grid */}
            <div className="p-4 sm:p-6">
              {/* Industry Solutions */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                  Industry Solutions
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {resources.industries.map((item, index) => (
                    <Link
                      key={index}
                      to={item.href}
                      className="group relative p-4 sm:p-6 bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 hover:border-blue-600 dark:hover:border-blue-400 hover:shadow-lg dark:hover:shadow-2xl hover:scale-105 transition-all duration-200 ease-out"
                      onClick={handleResourceClick}
                      style={{
                        transitionDelay: shouldShowDropdown ? `${index * 30}ms` : '0ms'
                      }}
                    >
                      <div className="relative">
                        <div className={`${item.color} mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-200`}>
                          {item.icon}
                        </div>
                        <h4 className="text-gray-900 dark:text-white font-semibold mb-2 text-sm sm:text-base group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                          {item.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-relaxed group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors duration-200">
                          {item.description}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Learning Resources */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                  Learning Resources
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {resources.blogs.map((item, index) => (
                    <Link
                      key={index + resources.industries.length}
                      to={item.href}
                      className="group relative p-4 sm:p-6 bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 hover:border-blue-600 dark:hover:border-blue-400 hover:shadow-lg dark:hover:shadow-2xl hover:scale-105 transition-all duration-200 ease-out"
                      onClick={handleResourceClick}
                      style={{
                        transitionDelay: shouldShowDropdown ? `${(index + resources.industries.length) * 30}ms` : '0ms'
                      }}
                    >
                      <div className="relative">
                        <div className={`${item.color} mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-200`}>
                          {item.icon}
                        </div>
                        <h4 className="text-gray-900 dark:text-white font-semibold mb-2 text-sm sm:text-base group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                          {item.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-relaxed group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors duration-200">
                          {item.description}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Business Resources */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                  Business Resources
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {resources.business.map((item, index) => (
                    <Link
                      key={index + resources.industries.length + resources.blogs.length}
                      to={item.href}
                      className="group relative p-4 sm:p-6 bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 hover:border-blue-600 dark:hover:border-blue-400 hover:shadow-lg dark:hover:shadow-2xl hover:scale-105 transition-all duration-200 ease-out"
                      onClick={handleResourceClick}
                      style={{
                        transitionDelay: shouldShowDropdown ? `${(index + resources.industries.length + resources.blogs.length) * 30}ms` : '0ms'
                      }}
                    >
                      <div className="relative">
                        <div className={`${item.color} mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-200`}>
                          {item.icon}
                        </div>
                        <h4 className="text-gray-900 dark:text-white font-semibold mb-2 text-sm sm:text-base group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                          {item.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-relaxed group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors duration-200">
                          {item.description}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};